// Core game engine: state machine, physics, input integration, scoring.
// Uses fixed-timestep updates so behavior is identical at any render framerate.

import {
  BASE_LINE_SCORE,
  BOARD_BUFFER_HEIGHT,
  BOARD_VISIBLE_HEIGHT,
  BOARD_WIDTH,
  COMBO_BONUS_PER,
  DEFAULT_GRAVITY_G,
  HARD_DROP_SCORE_PER_CELL,
  LINES_PER_LEVEL,
  LOCK_DELAY_MS,
  LOCK_RESET_MAX,
  NEXT_QUEUE_LENGTH,
  SOFT_DROP_SCORE_PER_CELL,
} from '../config';
import type {
  ActivePiece,
  GameStats,
  LineClearKind,
  LockResult,
  ModeContext,
  PieceType,
  Rotation,
} from '../types';
import { Bag } from './bag';
import { Board } from './board';
import { kicks, shapeOf, spawnPosition } from './pieces';
import { detectTSpin } from './tspin';

export type EngineEvent =
  | { type: 'spawn'; piece: ActivePiece }
  | { type: 'move'; dx: number; dy: number; auto: boolean }
  | { type: 'rotate'; from: Rotation; to: Rotation; kickIndex: number }
  | { type: 'hold'; piece: PieceType }
  | { type: 'softdrop'; cells: number }
  | { type: 'harddrop'; cells: number; piece: ActivePiece }
  | { type: 'lock'; result: LockResult; piece: ActivePiece; clearedRowCells?: ReadonlyArray<{ x: number; y: number; color: PieceType | 'G' }> }
  | { type: 'topout' }
  | { type: 'finish' }
  | { type: 'levelup'; level: number }
  /** Hold input rejected because the player already used hold on the
   *  current piece. Lets the UI shake the slot + play a reject sfx. */
  | { type: 'hold_rejected' };

export interface EngineConfig {
  dasMs: number;
  arrMs: number;
  softDropFactor: number; // multiplier vs gravity; Infinity = instant
  dcdMs: number; // delay after rotation/lock before DAS resumes
  ghostEnabled: boolean;
  initialLevel: number;
  preview: number;
  /** True for marathon: gravity increases with level. */
  levelGravity: boolean;
  /** When true, top-outs are suppressed (Zen). */
  noTopout: boolean;
  /** Optional seed for deterministic runs. */
  seed?: number;
}

export const DEFAULT_ENGINE_CONFIG: EngineConfig = {
  dasMs: 130,
  arrMs: 16,
  softDropFactor: 30,
  dcdMs: 0,
  ghostEnabled: true,
  initialLevel: 1,
  preview: NEXT_QUEUE_LENGTH,
  levelGravity: true,
  noTopout: false,
};

interface InputState {
  left: boolean;
  right: boolean;
  softDrop: boolean;
  // Per-direction DAS timer (ms since key pressed). -1 if not pressed.
  leftHeldMs: number;
  rightHeldMs: number;
  // Time since last ARR shift (ms).
  leftArrMs: number;
  rightArrMs: number;
}

export class Engine {
  readonly board = new Board();
  cfg: EngineConfig;
  bag: Bag;
  piece: ActivePiece | null = null;
  /** Incremented every time a fresh piece is spawned. Renderers use it to
   *  detect spawns deterministically (instead of comparing object identity). */
  pieceSeq = 0;
  /** Wall-clock timestamp (performance.now) when the current piece spawned.
   *  Read by the hard-drop guard to ignore slams within N ms of spawn. */
  pieceSpawnedAt = 0;
  /** Wall-clock timestamp at which the engine was paused, or 0 if running.
   *  Used to shift stats.startTime forward by the paused duration on
   *  resume — keeps timed modes (sprint / ultra) honest. */
  private pausedAt = 0;
  holdPiece: PieceType | null = null;
  holdLocked = false; // can only hold once per piece
  stats: GameStats;
  state: 'idle' | 'playing' | 'gameover' | 'finished' = 'idle';
  ctx: ModeContext = {};
  // Lock delay tracking.
  touchedMs = 0; // ms since piece first touched ground at current y (exposed for renderer pulse)
  private lockResets = 0;
  private lowestYReached = -Infinity;
  // Gravity accumulator (cells of pending fall).
  private gravityAccum = 0;
  // Rotation tracking for T-spin detection.
  private lastActionWasRotation = false;
  private lastKickIndex = -1;
  // Input.
  private input: InputState = {
    left: false, right: false, softDrop: false,
    leftHeldMs: -1, rightHeldMs: -1, leftArrMs: 0, rightArrMs: 0,
  };
  // Pending DCD timer.
  private dcdRemainingMs = 0;
  // Time when piece-spawn IRS/IHS should apply (pre-buffered rotate / hold).
  private bufferedRotate: 'cw' | 'ccw' | '180' | null = null;
  private bufferedHold = false;
  // Event sink.
  events: EngineEvent[] = [];

  constructor(cfg?: Partial<EngineConfig>) {
    this.cfg = { ...DEFAULT_ENGINE_CONFIG, ...cfg };
    this.bag = new Bag(this.cfg.seed);
    this.stats = this.newStats();
  }

  private newStats(): GameStats {
    return {
      score: 0,
      level: this.cfg.initialLevel,
      lines: 0,
      pieces: 0,
      startTime: performance.now(),
      elapsedMs: 0,
      combo: -1, // -1 = no combo active; goes to 0 on first non-clear after combo not started, this is okay
      backToBack: -1, // -1 = no B2B; >=0 means current B2B chain length
      tspins: 0,
      tspinsFull: 0,
      tspinsMini: 0,
      tspinSingles: 0,
      tspinDoubles: 0,
      tspinTriples: 0,
      tetrises: 0,
      perfectClears: 0,
      holds: 0,
      hardDrops: 0,
      hardDropCells: 0,
      finesse: 0,
      maxCombo: 0,
      maxB2B: 0,
    };
  }

  // ---------- public API ----------
  start(modeCtx: ModeContext = {}, initBoard?: (board: Board) => void): void {
    this.board.reset();
    this.ctx = modeCtx;
    this.bag = new Bag(this.cfg.seed);
    if (initBoard) initBoard(this.board);
    this.stats = this.newStats();
    this.piece = null;
    this.holdPiece = null;
    this.holdLocked = false;
    this.touchedMs = 0;
    this.lockResets = 0;
    this.gravityAccum = 0;
    this.lastActionWasRotation = false;
    this.lastKickIndex = -1;
    this.lowestYReached = -Infinity;
    this.input.leftHeldMs = -1;
    this.input.rightHeldMs = -1;
    this.input.softDrop = false;
    this.input.left = false;
    this.input.right = false;
    this.dcdRemainingMs = 0;
    this.bufferedRotate = null;
    this.bufferedHold = false;
    this.state = 'playing';
    this.events.length = 0;
    this.spawnNext();
  }

  stop(): void {
    this.state = 'idle';
  }

  /** Mark the engine as paused — records the timestamp so stats.startTime
   *  can be shifted forward on resume. Idempotent. */
  pauseTimer(): void {
    if (this.pausedAt === 0) this.pausedAt = performance.now();
  }

  /** Pair with pauseTimer(). Shifts stats.startTime forward by the
   *  paused duration so elapsedMs (computed as now - startTime) excludes
   *  the pause window. Idempotent. */
  resumeTimer(): void {
    if (this.pausedAt === 0) return;
    const delta = performance.now() - this.pausedAt;
    this.stats.startTime += delta;
    this.pausedAt = 0;
  }

  finish(): void {
    if (this.state !== 'playing') return;
    this.state = 'finished';
    this.events.push({ type: 'finish' });
  }

  setInput(
    next: Partial<{ left: boolean; right: boolean; softDrop: boolean }>,
    nowMs: number,
  ): void {
    if (next.left !== undefined) {
      if (next.left && !this.input.left) {
        this.input.leftHeldMs = 0; this.input.leftArrMs = 0;
        this.tryShift(-1);
        this.input.right = false; this.input.rightHeldMs = -1;
      } else if (!next.left && this.input.left) {
        this.input.leftHeldMs = -1;
      }
      this.input.left = next.left;
    }
    if (next.right !== undefined) {
      if (next.right && !this.input.right) {
        this.input.rightHeldMs = 0; this.input.rightArrMs = 0;
        this.tryShift(1);
        this.input.left = false; this.input.leftHeldMs = -1;
      } else if (!next.right && this.input.right) {
        this.input.rightHeldMs = -1;
      }
      this.input.right = next.right;
    }
    if (next.softDrop !== undefined) this.input.softDrop = next.softDrop;
  }

  rotate(direction: 'cw' | 'ccw' | '180'): void {
    if (this.state !== 'playing' || !this.piece) {
      // Buffer rotation for the next piece (IRS).
      this.bufferedRotate = direction;
      return;
    }
    const from = this.piece.rotation;
    let to: Rotation;
    if (direction === 'cw') to = ((from + 1) & 3) as Rotation;
    else if (direction === 'ccw') to = ((from + 3) & 3) as Rotation;
    else to = ((from + 2) & 3) as Rotation;
    const table = kicks(this.piece.type, from, to);
    for (let i = 0; i < table.length; i++) {
      const [dx, dy] = table[i]!;
      const candidate: ActivePiece = { ...this.piece, rotation: to, x: this.piece.x + dx, y: this.piece.y + dy };
      if (!this.board.collides(candidate)) {
        this.piece = candidate;
        this.lastActionWasRotation = true;
        this.lastKickIndex = i;
        this.events.push({ type: 'rotate', from, to, kickIndex: i });
        this.onSuccessfulAction();
        return;
      }
    }
  }

  hold(): void {
    if (this.state !== 'playing') {
      this.bufferedHold = true;
      return;
    }
    if (!this.piece) return;
    // Rejected because already-used this piece — emit an event so the UI
    // can shake the hold slot + play a rejection sfx. Caller can also
    // detect this by checking holdLocked beforehand.
    if (this.holdLocked) {
      this.events.push({ type: 'hold_rejected' });
      return;
    }
    const swapped = this.holdPiece;
    this.holdPiece = this.piece.type;
    this.holdLocked = true;
    this.stats.holds++;
    if (swapped) {
      this.spawn(swapped);
    } else {
      this.piece = null;
      this.spawnNext();
    }
    this.events.push({ type: 'hold', piece: this.holdPiece });
  }

  hardDrop(): void {
    if (this.state !== 'playing' || !this.piece) return;
    const d = this.board.dropDistance(this.piece);
    if (d > 0) {
      this.piece = { ...this.piece, y: this.piece.y + d };
      this.stats.score += d * HARD_DROP_SCORE_PER_CELL;
    }
    this.stats.hardDrops++;
    this.stats.hardDropCells += d;
    this.events.push({ type: 'harddrop', cells: d, piece: { ...this.piece } });
    this.lockPiece();
  }

  /** Per-frame update with delta time in ms. */
  update(dtMs: number): void {
    if (this.state !== 'playing' || !this.piece) return;
    if (this.dcdRemainingMs > 0) this.dcdRemainingMs = Math.max(0, this.dcdRemainingMs - dtMs);

    this.updateAutoShift(dtMs);
    this.applyGravity(dtMs);
    this.updateLockDelay(dtMs);
  }

  // ---------- internals ----------
  private spawnNext(): void {
    const type = this.bag.next();
    this.spawn(type);
  }

  private spawn(type: PieceType): void {
    const spawn = spawnPosition(type);
    const piece: ActivePiece = { type, rotation: 0, x: spawn.x, y: spawn.y };
    // If we can't spawn at this position, attempt to nudge up.
    if (this.board.collides(piece)) {
      // Try shifting up by 1 (lock-out).
      const lifted = { ...piece, y: piece.y - 1 };
      if (this.board.collides(lifted)) {
        if (!this.cfg.noTopout) {
          this.state = 'gameover';
          this.events.push({ type: 'topout' });
          return;
        }
        // In Zen mode, clear the bottom row and retry once.
        this.board.clearRows([BOARD_BUFFER_HEIGHT + BOARD_VISIBLE_HEIGHT - 1]);
      } else {
        piece.y = lifted.y;
      }
    }
    this.piece = piece;
    // NOTE: holdLocked is intentionally NOT cleared here; that's done in
    // lockPiece() so that a held swap can't immediately be re-held.
    this.touchedMs = 0;
    this.lockResets = 0;
    this.gravityAccum = 0;
    this.lowestYReached = piece.y;
    this.lastActionWasRotation = false;
    this.lastKickIndex = -1;
    this.stats.pieces++;
    this.pieceSeq++;
    this.pieceSpawnedAt = performance.now();
    this.events.push({ type: 'spawn', piece: { ...piece } });

    // Apply buffered rotate (IRS) and hold (IHS).
    if (this.bufferedHold) {
      this.bufferedHold = false;
      this.hold();
      // After hold, possibly apply buffered rotate to the new piece.
    }
    if (this.bufferedRotate) {
      const dir = this.bufferedRotate;
      this.bufferedRotate = null;
      this.rotate(dir);
    }
  }

  private tryShift(dx: number, auto = false): boolean {
    if (!this.piece) return false;
    const candidate = { ...this.piece, x: this.piece.x + dx };
    if (!this.board.collides(candidate)) {
      this.piece = candidate;
      this.events.push({ type: 'move', dx, dy: 0, auto });
      this.lastActionWasRotation = false;
      this.onSuccessfulAction();
      return true;
    }
    return false;
  }

  private onSuccessfulAction(): void {
    if (!this.piece) return;
    // If the piece is currently grounded, allow a lock-delay reset (bounded).
    const grounded = this.board.collides({ ...this.piece, y: this.piece.y + 1 });
    if (grounded) {
      if (this.lockResets < LOCK_RESET_MAX) {
        this.lockResets++;
        this.touchedMs = 0;
      }
    } else {
      // If piece moved off ground, clear touched timer.
      this.touchedMs = 0;
    }
  }

  private updateAutoShift(dtMs: number): void {
    // DAS: after key held > dasMs, repeat at ARR.
    if (this.dcdRemainingMs > 0) return;
    if (this.input.left) {
      this.input.leftHeldMs += dtMs;
      if (this.input.leftHeldMs >= this.cfg.dasMs) {
        if (this.cfg.arrMs <= 0) {
          // Instant: shift as far as possible.
          while (this.tryShift(-1, true)) { /* keep shifting */ }
        } else {
          this.input.leftArrMs += dtMs;
          while (this.input.leftArrMs >= this.cfg.arrMs) {
            this.input.leftArrMs -= this.cfg.arrMs;
            if (!this.tryShift(-1, true)) { this.input.leftArrMs = 0; break; }
          }
        }
      }
    }
    if (this.input.right) {
      this.input.rightHeldMs += dtMs;
      if (this.input.rightHeldMs >= this.cfg.dasMs) {
        if (this.cfg.arrMs <= 0) {
          while (this.tryShift(1, true)) { /* keep shifting */ }
        } else {
          this.input.rightArrMs += dtMs;
          while (this.input.rightArrMs >= this.cfg.arrMs) {
            this.input.rightArrMs -= this.cfg.arrMs;
            if (!this.tryShift(1, true)) { this.input.rightArrMs = 0; break; }
          }
        }
      }
    }
  }

  private gravityCellsPerMs(): number {
    // gravityG is "cells per 60Hz frame". Convert to cells/ms.
    const level = Math.max(1, this.stats.level);
    // Tetris Worlds-style: gravity = (0.8 - (level-1)*0.007)^(level-1) seconds per cell. Cells/sec = 1/that.
    // We'll use this only when levelGravity is enabled.
    if (!this.cfg.levelGravity) return DEFAULT_GRAVITY_G * 60 / 1000;
    const base = Math.pow(0.8 - (level - 1) * 0.007, Math.max(0, level - 1));
    const cellsPerSec = 1 / Math.max(base, 1e-6);
    return cellsPerSec / 1000;
  }

  private applyGravity(dtMs: number): void {
    if (!this.piece) return;
    const baseRate = this.gravityCellsPerMs();
    const rate = this.input.softDrop
      ? (Number.isFinite(this.cfg.softDropFactor) ? baseRate * this.cfg.softDropFactor : Infinity)
      : baseRate;
    if (rate === Infinity) {
      const d = this.board.dropDistance(this.piece);
      if (d > 0) {
        this.piece = { ...this.piece, y: this.piece.y + d };
        if (this.input.softDrop) this.stats.score += d * SOFT_DROP_SCORE_PER_CELL;
        this.lastActionWasRotation = false;
      }
    } else {
      this.gravityAccum += rate * dtMs;
      while (this.gravityAccum >= 1) {
        this.gravityAccum -= 1;
        if (!this.piece) break;
        const candidate: ActivePiece = { ...this.piece, y: this.piece.y + 1 };
        if (this.board.collides(candidate)) break;
        this.piece = candidate;
        if (this.input.softDrop) this.stats.score += SOFT_DROP_SCORE_PER_CELL;
        this.lastActionWasRotation = false;
      }
    }
    // Reset lock-reset counter if piece dropped to a new lowest row.
    if (this.piece && this.piece.y > this.lowestYReached) {
      this.lowestYReached = this.piece.y;
      this.lockResets = 0;
      this.touchedMs = 0;
    }
  }

  private updateLockDelay(dtMs: number): void {
    if (!this.piece) return;
    const grounded = this.board.collides({ ...this.piece, y: this.piece.y + 1 });
    if (!grounded) { this.touchedMs = 0; return; }
    this.touchedMs += dtMs;
    if (this.touchedMs >= LOCK_DELAY_MS) {
      this.lockPiece();
    }
  }

  private lockPiece(): void {
    if (!this.piece) return;
    // Detect T-spin before locking.
    const tspin = detectTSpin(this.board, this.piece, this.lastActionWasRotation, this.lastKickIndex);
    this.board.lock(this.piece);
    const lockedPiece = { ...this.piece };
    const cleared = this.board.fullRows();
    const lineCount = cleared.length;
    // Capture cells for particle/animation effects.
    const clearedCells: { x: number; y: number; color: PieceType | 'G' }[] = [];
    for (const row of cleared) {
      for (let x = 0; x < BOARD_WIDTH; x++) {
        const c = this.board.at(x, row);
        if (c) clearedCells.push({ x, y: row, color: c });
      }
    }
    this.board.clearRows(cleared);
    const isPerfect = this.board.isEmpty() && lineCount > 0;
    const kind = classifyClear(lineCount, tspin.kind);
    let scoreDelta = 0;
    if (kind) {
      scoreDelta += (BASE_LINE_SCORE[kind] ?? 0) * this.stats.level;
    }
    if (lineCount > 0) {
      this.stats.combo += 1;
      if (this.stats.combo > 0) {
        scoreDelta += this.stats.combo * COMBO_BONUS_PER * this.stats.level;
      }
    } else {
      this.stats.combo = -1;
    }
    // B2B: tetris or t-spin (full/mini with clears) extends; any normal clear breaks.
    const isB2BClear = lineCount > 0 && (kind === 'tetris' || tspin.kind !== 'none');
    let isB2BBonus = false;
    if (isB2BClear) {
      if (this.stats.backToBack >= 0) {
        scoreDelta = Math.round(scoreDelta * 1.5);
        isB2BBonus = true;
      }
      this.stats.backToBack = Math.max(0, this.stats.backToBack) + 1;
    } else if (lineCount > 0) {
      this.stats.backToBack = -1;
    }
    if (isPerfect) {
      const pcKey = lineCount === 1 ? 'perfect_clear_single'
        : lineCount === 2 ? 'perfect_clear_double'
        : lineCount === 3 ? 'perfect_clear_triple'
        : lineCount === 4 && isB2BBonus ? 'back_to_back_perfect_clear_tetris'
        : 'perfect_clear_tetris';
      scoreDelta += (BASE_LINE_SCORE[pcKey] ?? 0) * this.stats.level;
      this.stats.perfectClears++;
    }
    if (tspin.kind !== 'none') {
      this.stats.tspins++;
      if (tspin.kind === 'full') {
        this.stats.tspinsFull++;
        if (lineCount === 1) this.stats.tspinSingles++;
        else if (lineCount === 2) this.stats.tspinDoubles++;
        else if (lineCount === 3) this.stats.tspinTriples++;
      } else if (tspin.kind === 'mini') {
        this.stats.tspinsMini++;
      }
    }
    if (lineCount === 4) this.stats.tetrises++;
    if (this.stats.combo > this.stats.maxCombo) this.stats.maxCombo = this.stats.combo;
    if (this.stats.backToBack > this.stats.maxB2B) this.stats.maxB2B = this.stats.backToBack;
    this.stats.score += scoreDelta;
    this.stats.lines += lineCount;

    const oldLevel = this.stats.level;
    const targetLevel = Math.max(this.cfg.initialLevel, Math.floor(this.stats.lines / LINES_PER_LEVEL) + this.cfg.initialLevel);
    const newLevel = Math.max(this.stats.level, targetLevel);
    const levelUp = newLevel !== oldLevel;
    if (levelUp) this.stats.level = newLevel;

    const result: LockResult = {
      cleared,
      kind,
      isPerfectClear: isPerfect,
      isBackToBack: isB2BBonus,
      comboCount: Math.max(0, this.stats.combo),
      tSpinKind: tspin.kind,
      scoreDelta,
      levelUp,
      newLevel,
    };
    this.events.push({ type: 'lock', result, piece: lockedPiece, clearedRowCells: clearedCells });
    // Only fire the levelup event when gravity actually changes — otherwise
    // sprint / ultra / zen would show a misleading "level up" notification
    // with no speed bump behind it.
    if (levelUp && this.cfg.levelGravity) this.events.push({ type: 'levelup', level: newLevel });

    this.piece = null;
    this.holdLocked = false;
    this.dcdRemainingMs = this.cfg.dcdMs;
    this.spawnNext();
  }
}

function classifyClear(lines: number, tspin: 'none' | 'mini' | 'full'): LineClearKind | null {
  if (tspin === 'full') {
    if (lines === 0) return 'tspin';
    if (lines === 1) return 'tspin_single';
    if (lines === 2) return 'tspin_double';
    if (lines === 3) return 'tspin_triple';
  }
  if (tspin === 'mini') {
    if (lines === 0) return 'mini_tspin';
    if (lines === 1) return 'mini_tspin_single';
    if (lines === 2) return 'mini_tspin_double';
  }
  if (lines === 1) return 'single';
  if (lines === 2) return 'double';
  if (lines === 3) return 'triple';
  if (lines === 4) return 'tetris';
  return null;
}
